import { customersQuery } from "@common/api"
import { squawkFilterOptionsQuery } from "@common/api/quoting/squawk"
import { useUsers } from "@common/api/users"
import type {
  FilterFieldConfig,
  FilterOptionValue,
} from "@common/components/filters/filter-config"
import { useQuery } from "@tanstack/react-query"
import { CUSTOMER_LIMIT } from "@/routes/_layout/quoting_/quotes/-components/filters/filter-model"
import AssignmentOutline from "~icons/material-symbols/assignment-outline"
import GroupOutline from "~icons/material-symbols/group-outline"
import MenuBookOutline from "~icons/material-symbols/menu-book-outline"
import PersonOutline from "~icons/material-symbols/person-outline"
import RadioButtonChecked from "~icons/material-symbols/radio-button-checked"
import WarningOutline from "~icons/material-symbols/warning-outline"
import { SeverityIcon } from "../-components/severity-icon"
import { useSquawkList } from "../-components/squawk-list"
import { StageIcon } from "../-components/status-icon"
import {
  type SquawksSearch,
  severityFilterSchema,
  statusFilterSchema,
} from "./search"
import {
  NO_SEVERITY,
  SEVERITIES,
  STATUSES,
  severityLabel,
  statusLabel,
  statusStage,
} from "./status"

export type SquawkFilterValues = Pick<
  SquawksSearch,
  "status" | "severity" | "work_order" | "customer" | "reporter" | "ata"
>

export const NO_SQUAWK_FILTERS: SquawkFilterValues = {
  status: undefined,
  severity: undefined,
  work_order: undefined,
  customer: undefined,
  reporter: undefined,
  ata: undefined,
}

export function hasSquawkFilters(values: SquawkFilterValues): boolean {
  return Boolean(
    values.status?.length ||
      values.severity?.length ||
      values.work_order?.length ||
      values.customer?.length ||
      values.reporter?.length ||
      values.ata?.length,
  )
}

function useSquawkFilterOptions() {
  const { options } = useSquawkList()
  return useQuery(squawkFilterOptionsQuery(options))
}

const statusFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "status",
  label: "Status",
  icon: RadioButtonChecked,
  type: "multiselect",
  searchPlaceholder: "Search statuses",
  emptyLabel: "No statuses",
  useOptions: () => ({
    options: STATUSES.map((status) => ({
      value: status,
      label: statusLabel(status),
      icon: <StageIcon stage={statusStage(status)} />,
    })),
    isLoading: false,
  }),
  get: (values) => values.status,
  set: (next) => ({
    status: next?.flatMap((value) => {
      const parsed = statusFilterSchema.safeParse(value)
      return parsed.success ? [parsed.data] : []
    }),
  }),
}

const severityFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "severity",
  label: "Severity",
  icon: WarningOutline,
  type: "multiselect",
  searchPlaceholder: "Search severities",
  emptyLabel: "No severities",
  useOptions: () => ({
    options: SEVERITIES.map((severity) => ({
      value: severity ?? NO_SEVERITY,
      label: severityLabel(severity),
      icon: <SeverityIcon severity={severity} />,
    })),
    isLoading: false,
  }),
  get: (values) => values.severity,
  set: (next) => ({
    severity: next?.flatMap((value) => {
      const parsed = severityFilterSchema.safeParse(value)
      return parsed.success ? [parsed.data] : []
    }),
  }),
}

const workOrderFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "work_order",
  label: "Work order",
  icon: AssignmentOutline,
  type: "multiselect",
  searchPlaceholder: "Search work orders",
  emptyLabel: "No work orders",
  useOptions: () => {
    const { data, isLoading } = useSquawkFilterOptions()
    return {
      options: (data?.work_orders ?? []).map((number) => ({
        value: number,
        label: number,
      })),
      isLoading,
    }
  },
  get: (values) => values.work_order,
  set: (next) => ({ work_order: next?.map(String) }),
}

const reporterFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "reporter",
  label: "Reported by",
  icon: PersonOutline,
  type: "multiselect",
  searchPlaceholder: "Search people",
  emptyLabel: "Unassigned",
  useOptions: () => {
    const { users, isLoading } = useUsers()
    return {
      options: users.map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name}`.trim(),
      })),
      isLoading,
    }
  },
  get: (values) => values.reporter,
  set: (next) => ({ reporter: next?.map(String) }),
}

function chapters(
  values: FilterOptionValue[] | undefined,
): number[] | undefined {
  return values?.flatMap((value) => (typeof value === "number" ? [value] : []))
}

const ataFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "ata",
  label: "ATA",
  icon: MenuBookOutline,
  type: "multiselect",
  searchPlaceholder: "Search chapters",
  emptyLabel: "No chapters",
  useOptions: () => {
    const { data, isLoading } = useSquawkFilterOptions()
    return {
      options: (data?.ata_chapters ?? []).map((chapter) => ({
        value: chapter,
        label: `ATA ${String(chapter).padStart(2, "0")}`,
      })),
      isLoading,
    }
  },
  get: (values) => values.ata,
  set: (next) => ({ ata: chapters(next) }),
}

const customerFilter: FilterFieldConfig<SquawkFilterValues> = {
  key: "customer",
  label: "Customer",
  icon: GroupOutline,
  type: "multiselect",
  searchPlaceholder: "Search customers",
  emptyLabel: "Unassigned",
  useOptions: () => {
    const { data, isLoading } = useQuery(
      customersQuery({ skip: 0, limit: CUSTOMER_LIMIT }),
    )
    return {
      options: (data?.data ?? []).map((customer) => ({
        value: customer.id,
        label: customer.name ?? customer.email ?? customer.id,
      })),
      isLoading,
    }
  },
  get: (values) => values.customer,
  set: (next) => ({ customer: next?.map(String) }),
}

export const squawkFilterConfigs: FilterFieldConfig<SquawkFilterValues>[] = [
  statusFilter,
  severityFilter,
  workOrderFilter,
  customerFilter,
  reporterFilter,
  ataFilter,
]
